import { Schema } from "@upserve/cold-storage";
import { Maybe } from "true-myth";
import {
  find,
  flatten,
  forEach,
  isEmpty,
  map,
  omit,
  pipe,
  prop,
  propEq,
  reduce,
  uniqBy,
} from "ramda";
import { eachDay, isSameDay, isAfter, isBefore } from "date-fns";

import {
  IAssigneeProfileResponse,
  IScheduleResponse,
  ISchedulesResponse,
  IShiftResponse,
} from "interfaces/schedule";
import { maybetype } from "helpers/schema";
import {
  addPersonToSchedule,
  findByUuid,
  personSchema,
  updatePersonInSchedule,
  sortAssigneesShifts,
} from "helpers/transforms/people";
import { roleSchema, posUserSchema } from "helpers/transforms/pos_users";
import { IPeople, IPerson, IPosUser, IRole, ISchedule } from "types/schedules";
import { ITemplatePerson } from "types/templates";

const { parseBySchema, jsontype, encodeBySchema } = Schema;

const scheduleSchema: ITransformedResponse = {
  updatedAt: maybetype(jsontype("dateTime")),
  date: jsontype("date"),
  people: [personSchema],
};

interface ITransformedResponse {
  updatedAt: Maybe<Date>;
  date: Date;
  people: IPeople;
}

function isUnassignedShiftGroup(person: IPerson | ITemplatePerson): boolean {
  return propEq("assignee", Maybe.nothing())(person);
}

function mergeUnassignedShiftGroup(
  shiftGroup: IPerson,
  newCompleteSchedule: ISchedule
): ISchedule {
  const existingUnassignedShiftGroup: Maybe<IPerson> = Maybe.of(
    find(isUnassignedShiftGroup, newCompleteSchedule.people)
  );

  return existingUnassignedShiftGroup.match({
    Just: (unassignedShiftGroup: IPerson) => {
      // There are already unassigned shifts in our schedule for other days
      // And we will need to merge the unassigned shifts from the new day schedule into
      // those

      return updatePersonInSchedule(
        newCompleteSchedule,
        shiftGroup,
        unassignedShiftGroup
      );
    },
    // First day in our complete schedule with unassigned shifts
    Nothing: () => addPersonToSchedule(newCompleteSchedule, shiftGroup),
  });
}

function mergeAssignedShiftGroup(
  assignee: IPosUser,
  shiftGroup: IPerson,
  newCompleteSchedule: ISchedule
): ISchedule {
  // real person as opposed to unassigned
  const existingShiftGroupWithAssignee: Maybe<IPerson> = findByUuid(
    assignee.uuid,
    newCompleteSchedule.people
  );

  return existingShiftGroupWithAssignee.match({
    // There are already unassigned shifts in our schedule for other days
    // And we will need to merge the unassigned shifts from the new day schedule into
    // those
    Just: (existingAssignee: IPerson) =>
      updatePersonInSchedule(newCompleteSchedule, shiftGroup, existingAssignee),
    // First day in our complete schedule that this person has shifts
    Nothing: () => addPersonToSchedule(newCompleteSchedule, shiftGroup),
  });
}

function mergeDayScheduleUpdatedAt(
  newUpdatedAt: Date,
  parsedDaySchedule: ITransformedResponse,
  newCompleteSchedule: ISchedule
): ISchedule {
  return newCompleteSchedule.updatedAt.match({
    Just: (existingUpdatedAt: Date) => {
      if (isAfter(newUpdatedAt, existingUpdatedAt)) {
        return {
          ...newCompleteSchedule,
          updatedAt: parsedDaySchedule.updatedAt,
        };
      }
      return newCompleteSchedule;
    },
    Nothing: () => ({
      ...newCompleteSchedule,
      updatedAt: parsedDaySchedule.updatedAt,
    }),
  });
}

function validateAndMergeUpdatedAt(
  parsedDaySchedule: ITransformedResponse,
  newCompleteSchedule: ISchedule
): ISchedule {
  return parsedDaySchedule.updatedAt.match({
    Just: (newUpdatedAt: Date) => {
      return mergeDayScheduleUpdatedAt(
        newUpdatedAt,
        parsedDaySchedule,
        newCompleteSchedule
      );
    },
    Nothing: () => newCompleteSchedule,
  });
}

function matchRoleCategories(schedule: ISchedule, people: IPeople): ISchedule {
  const allRoles = pipe(
    map((person: IPerson) => person.assignee.map((a) => a.roles).unwrapOr([])),
    (roles: IRole[][]) => flatten<IRole>(roles),
    uniqBy(prop("uuid"))
  )(people);

  const peopleWithMatchedRoles = map((schedulePerson) => {
    let roleMatched = false;
    const shiftsWithMatchedCat = map((shift) => {
      if (shift.role.category) {
        // No changes required, shift already has category populated
        return shift;
      }
      // Find this role by checking all known roles
      const matchedRole = allRoles.find(propEq("uuid", shift.role.uuid));
      if (matchedRole) {
        roleMatched = true;
        return {
          ...shift,
          role: matchedRole,
        };
      }
      return shift;
    }, schedulePerson.shifts);
    return roleMatched
      ? ({
          ...schedulePerson,
          shifts: shiftsWithMatchedCat,
        } as IPerson)
      : schedulePerson;
  }, schedule.people);

  return {
    ...schedule,
    people: peopleWithMatchedRoles,
  };
}

interface IShiftWithMergedDate extends IShiftResponse {
  date: string;
}

interface IAdjustedShiftSet {
  assignee?: IAssigneeProfileResponse;
  shifts: IShiftWithMergedDate[];
}

function appendShiftDate(
  shift: IShiftResponse,
  date: string
): IShiftWithMergedDate {
  return {
    ...shift,
    date,
  };
}

function parseScheduleSchema(
  daySchedule: IScheduleResponse
): ITransformedResponse {
  const peopleWithAdjustedShifts: IAdjustedShiftSet[] = map((person) => {
    return {
      ...person,
      shifts: map(
        (shift: IShiftResponse) => appendShiftDate(shift, daySchedule.date),
        person.shifts
      ),
    };
  }, daySchedule.people);

  const schedule = { ...daySchedule, people: peopleWithAdjustedShifts };
  return parseBySchema(scheduleSchema)(schedule);
}

function mergePeopleIntoSchedule(
  people: IPeople,
  schedule: ISchedule
): ISchedule {
  let mergedSchedule = schedule;

  forEach((shiftGroup: IPerson): void => {
    shiftGroup.assignee.match({
      Just: (assignee: IPosUser) => {
        mergedSchedule = mergeAssignedShiftGroup(
          assignee,
          shiftGroup,
          mergedSchedule
        );
      },
      Nothing: () => {
        mergedSchedule = mergeUnassignedShiftGroup(shiftGroup, mergedSchedule);
      },
    });
  }, people);

  return mergedSchedule;
}

const EMPTY_SCHEDULE: ISchedule = {
  startDate: Maybe.nothing(),
  endDate: Maybe.nothing(),
  updatedAt: Maybe.nothing(),
  people: [],
};

function mergeManySchedules(
  daySchedules: ITransformedResponse[],
  completeSchedule: ISchedule = EMPTY_SCHEDULE
): ISchedule {
  let earliestDate = completeSchedule.startDate;
  let latestDate = completeSchedule.endDate;
  const merged = reduce(
    (mergedSchedule, thisDay: ITransformedResponse) => {
      // Track start and end date by checking if current day is within bounds
      // Use thisDay.date as the default of mapOr()
      const newEarliest = earliestDate.mapOr(thisDay.date, (earliest) =>
        isBefore(thisDay.date, earliest) ? thisDay.date : earliest
      );
      const newLatest = latestDate.mapOr(thisDay.date, (latest) =>
        isAfter(thisDay.date, latest) ? thisDay.date : latest
      );
      earliestDate = Maybe.of(newEarliest);
      latestDate = Maybe.of(newLatest);

      const mergedWithUpdatedAt = validateAndMergeUpdatedAt(
        thisDay,
        mergedSchedule
      );
      return mergePeopleIntoSchedule(thisDay.people, mergedWithUpdatedAt);
    },
    completeSchedule,
    daySchedules
  );
  return {
    ...merged,
    startDate: earliestDate,
    endDate: latestDate,
  };
}

// SERIALIZATION -------------------------------------------

const encodeScheduleToJSON = encodeBySchema(scheduleSchema);

function splitScheduleByDays(schedule: ISchedule): ITransformedResponse[] {
  const scheduleRange = Maybe.all(schedule.startDate, schedule.endDate).match({
    Just: ([start, end]: Date[]) => eachDay(start, end),
    Nothing: () => [],
  });

  return scheduleRange.map((date) => {
    const filteredPeople: IPerson[] = schedule.people.reduce(
      (people: IPerson[], person: IPerson): IPerson[] => {
        const filteredShifts = person.shifts.filter((shift) =>
          isSameDay(date, shift.startTime)
        );

        if (!isEmpty(filteredShifts)) {
          people.push({
            ...person,
            shifts: filteredShifts,
          });
        }

        return people;
      },
      []
    );

    return {
      updatedAt: Maybe.nothing<Date>(),
      date,
      people: filteredPeople,
    };
  });
}

function dropObsoleteShiftTimeFormat(
  encodedDays: ISchedulesResponse
): ISchedulesResponse {
  return map((schedule) => {
    return {
      ...schedule,
      people: map((person) => {
        return {
          ...person,
          shifts: map(
            (shift) => omit(["startTime", "endTime"], shift),
            person.shifts
          ),
        };
      }, schedule.people),
    };
  }, encodedDays);
}

function encodeSchedule(schedule: ISchedule): ISchedulesResponse {
  const encodedDays = pipe(
    splitScheduleByDays,
    map(encodeScheduleToJSON)
  )(schedule);

  return dropObsoleteShiftTimeFormat(encodedDays as ISchedulesResponse);
}

export {
  EMPTY_SCHEDULE,
  encodeSchedule,
  matchRoleCategories,
  isUnassignedShiftGroup,
  ITransformedResponse,
  IShiftWithMergedDate,
  mergeManySchedules,
  mergePeopleIntoSchedule,
  mergeUnassignedShiftGroup,
  parseScheduleSchema,
  posUserSchema,
  roleSchema,
  sortAssigneesShifts,
  splitScheduleByDays,
  validateAndMergeUpdatedAt,
};
